import { useQuery } from '@apollo/client';
import type { MouseEvent, FC } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  EntityPageFocusType,
  EntityPagePublicationStatus,
} from '@xing-com/crate-common-graphql-types';
import {
  COMPANY_PROFILE_CREATION_URL,
  BRANDMANAGER_URL,
  relocate,
} from '@xing-com/crate-companies-shared';
import { IconEdit, IconPlus } from '@xing-com/icons';

import { SectionBrandmanagerDocument } from '../../graphql/queries/section-brandmanager.gql-types';
import { useNwtTrackingCompanies } from '../../utils/tracking';
import { SectionGridContainer } from '../section-container/section-container.styled';

import * as Styled from './section-brandmanager.styles';

type BrandmanagerAction = {
  label: string;
  icon?: typeof IconEdit | typeof IconPlus;
  trackingElement:
    | 'create_company_button'
    | 'finish_company_creation_button'
    | 'brand_manager_button';
  to: string;
};

type BrandmanagerViewModel = {
  actions: [BrandmanagerAction] | [BrandmanagerAction, BrandmanagerAction];
  bodyCopyId?: string;
};

const createAction = (hasOtherProfiles?: boolean): BrandmanagerAction => ({
  icon: hasOtherProfiles ? IconPlus : undefined,
  label: hasOtherProfiles
    ? 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.CREATE_ANOTHER'
    : 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.CREATE',
  trackingElement: 'create_company_button',
  to: COMPANY_PROFILE_CREATION_URL,
});

const finalizeAction = (hasOtherProfiles?: boolean): BrandmanagerAction => ({
  icon: hasOtherProfiles ? IconPlus : undefined,
  label: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.FINALIZE',
  trackingElement: 'finish_company_creation_button',
  to: COMPANY_PROFILE_CREATION_URL,
});

const manageAction = (): BrandmanagerAction => ({
  icon: IconEdit,
  label: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BUTTON.MANAGE',
  trackingElement: 'brand_manager_button',
  to: BRANDMANAGER_URL,
});

export const SectionBrandmanager: FC = () => {
  const { $t } = useIntl();
  const trackNwtEvent = useNwtTrackingCompanies();

  const [viewModel, setViewModel] = useState<BrandmanagerViewModel | null>(
    null
  );

  useQuery(SectionBrandmanagerDocument, {
    onCompleted: (data) => {
      const teampermissionsResources =
        data?.viewer?.teamPermissions?.userResources?.collection ?? [];

      const userEntityPages = data?.viewer?.userEntityPages?.collection ?? [];
      const companyInCreation = userEntityPages.find(
        (entityPage) =>
          entityPage?.focusType === EntityPageFocusType.Company &&
          entityPage?.publicationStatus === EntityPagePublicationStatus.Created
      );

      const hasManageableCompanyProfile = teampermissionsResources.length > 0;

      if (hasManageableCompanyProfile && companyInCreation) {
        setViewModel({
          actions: [
            finalizeAction(hasManageableCompanyProfile),
            manageAction(),
          ],
        });
      } else if (hasManageableCompanyProfile) {
        setViewModel({
          actions: [createAction(hasManageableCompanyProfile), manageAction()],
        });
      } else if (companyInCreation) {
        setViewModel({
          actions: [finalizeAction()],
          bodyCopyId: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BOX.FINALIZE',
        });
      } else {
        setViewModel({
          actions: [createAction()],
          bodyCopyId: 'CP_OVERVIEW_BRANDMANAGER_SECTION_BOX.CREATE',
        });
      }
    },
  });

  const handleOnClick = (
    action: BrandmanagerAction,
    ev: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    ev.stopPropagation();
    ev.preventDefault();

    relocate(action.to);

    trackNwtEvent({
      eventSchema: 'extended',
      page: 'companies/overview',
      event: 'opened',
      element: action.trackingElement,
      moduleTitle: 'brand_manager',
    });
  };

  return (
    <Styled.CenterContainer data-testid="SECTION-BRANDMANAGER">
      <SectionGridContainer variant="FullSize">
        <Styled.GridItem span={12}>
          {viewModel && (
            <Styled.LinkWrapper>
              {viewModel.bodyCopyId && (
                <Styled.BodyCopy>
                  {$t({ id: viewModel.bodyCopyId })}
                </Styled.BodyCopy>
              )}

              <Styled.ActionsWrapper>
                {viewModel.actions.map((action, index) =>
                  index === 0 && viewModel.actions.length === 2 ? (
                    <Styled.TextButton
                      key={action.trackingElement}
                      icon={action.icon}
                      to={action.to}
                      onClick={(ev) => handleOnClick(action, ev)}
                    >
                      {$t({ id: action.label })}
                    </Styled.TextButton>
                  ) : (
                    <Styled.Button
                      key={action.trackingElement}
                      icon={action.icon}
                      to={action.to}
                      onClick={(ev) => handleOnClick(action, ev)}
                      variant="secondary"
                      size="medium"
                    >
                      {$t({ id: action.label })}
                    </Styled.Button>
                  )
                )}
              </Styled.ActionsWrapper>
            </Styled.LinkWrapper>
          )}
          <Styled.SmallDevice variant="info">
            {/** @ts-expect-error dangerous set inner html used */}
            <label
              dangerouslySetInnerHTML={{
                __html: $t({ id: 'CP_LANDING_MOBILE_BANNER' }),
              }}
            />
          </Styled.SmallDevice>
        </Styled.GridItem>
      </SectionGridContainer>
    </Styled.CenterContainer>
  );
};
